import { useCallback, useEffect, useMemo, useState } from "react";

import { ModelIcon } from "@hightouch/htui";
import { useFlags } from "launchdarkly-react-client-sdk";

import {
  AGENT_CONTEXT,
  type AgentContext,
  type PageBrand,
  usePublishAgentContext,
} from "src/components/llm/agent-focus/agent-focus";
import type { ComposerContextChip } from "src/components/llm/components/composer-context-chip";
import { SKILLS_PATH } from "src/components/llm/context-usage/utils";
import {
  type AgentPromptChip,
  useAgentControlContext,
} from "src/components/llm/embedded-chat/agent-control-context";
import type { EmbeddedComposerExtras } from "src/components/llm/embedded-chat/embedded-agent-chat";
import { useOpenInNewTab } from "src/hooks/use-open-in-new-tab";
import { useCreativeBoostEnabled } from "src/pages/ad-studio/components/use-creative-boost-enabled";
import { useBrandProfileOptions } from "src/pages/agents/hooks/use-brand-profile-options";
import { BrandScopePill } from "src/pages/projects/amp-composer/brand-scope-pill";
import { ComposerAttachments } from "src/pages/projects/amp-composer/composer-attachments";
import {
  selectionsFromInitialContext,
  toAmpAgentContext,
  toInteractionContext,
} from "src/pages/projects/amp-composer/composer-context";
import { ComposerControls } from "src/pages/projects/amp-composer/composer-controls";
import {
  type ComposerSelections,
  clearSentSelections,
  DEFAULT_SELECTIONS,
} from "src/pages/projects/amp-composer/composer-selections";
import { emptyOptionsTooltip } from "src/pages/projects/amp-composer/empty-options-tooltip";
import { skillsMenuItem } from "src/pages/projects/amp-composer/skills-menu-item";
import {
  type ComposerChannels,
  useComposerOptions,
} from "src/pages/projects/amp-composer/use-composer-options";
import { useFileTypeAccess } from "src/pages/projects/library/files/use-file-type-access";

export type AmpComposerOptions = {
  /** The brand of the page beside the chat, which limits every brand-specific option. */
  pageBrand: PageBrand | undefined;
  /** Chips for what the page sends with the next message, such as selected files. */
  contextChips?: readonly ComposerContextChip[];
  /**
   * Shares the picks with the page's chat, and takes page nudges such as a prompt chip.
   * Leave on, unless this input keeps its own picks and hands them to a chat elsewhere,
   * as the Library home does.
   */
  publishContext?: boolean;
};

type ComposerAgentContext = Pick<
  AgentContext,
  "ads" | "emails" | "selectedSkills"
>;

/**
 * The AMP chat's composer, the same on every page and in new and open chats.
 * The picks live in one state; the brand scope, what the controls offer, and
 * what the next message sends derive from the picks and the loaded options.
 */
export function useAmpComposer({
  pageBrand,
  contextChips,
  publishContext = true,
}: AmpComposerOptions): {
  composer: EmbeddedComposerExtras;
  /** What the next message sends, for a composer that hands it off itself. */
  context: ComposerAgentContext;
  onSessionChange: (sessionId: string | null) => void;
  /** Puts every pick back to its default. */
  reset: () => void;
} {
  const access = useFileTypeAccess();
  const channels = useMemo(
    (): ComposerChannels => ({ ad: access.ad, email: access.email }),
    [access.ad, access.email],
  );
  const isCreativeBoostEnabled = useCreativeBoostEnabled();
  const isVideoEngineEnabled = Boolean(
    useFlags().adStudioShowVideoEngineToggle,
  );
  const sendSettings = useMemo(
    () => ({ channels, isCreativeBoostEnabled, isVideoEngineEnabled }),
    [channels, isCreativeBoostEnabled, isVideoEngineEnabled],
  );
  const openInNewTab = useOpenInNewTab();
  const { brandProfiles } = useBrandProfileOptions();

  const [selections, setSelections] = useState(DEFAULT_SELECTIONS);
  const onChange = useCallback((change: Partial<ComposerSelections>) => {
    setSelections((current) => ({ ...current, ...change }));
  }, []);
  const [promptChip, setPromptChip] = useState<AgentPromptChip | null>(null);
  const { registerNudgeListener } = useAgentControlContext();
  // Only the page's chat takes a page's nudges; an input that hands off to a chat elsewhere would show a second chip.
  useEffect(() => {
    if (!publishContext) return;
    return registerNudgeListener((_prompt, metadata) => {
      if (!metadata?.promptChip) return false;
      setPromptChip(metadata.promptChip);
      return true;
    });
  }, [publishContext, registerNudgeListener]);

  const { options, selectedOptions, brandScope, isLoading } =
    useComposerOptions({
      channels,
      selections,
      pageBrandProfileId: pageBrand?.brandProfileId,
    });
  const context = useMemo(
    () => toAmpAgentContext(selectedOptions, sendSettings),
    [selectedOptions, sendSettings],
  );
  const clearPromptChip = useCallback(() => setPromptChip(null), []);
  // Undefined publishes nothing and clears nothing.
  usePublishAgentContext(
    AGENT_CONTEXT.selectedSkills,
    publishContext ? context.selectedSkills : undefined,
  );
  usePublishAgentContext(
    AGENT_CONTEXT.ads,
    publishContext ? context.ads : undefined,
  );
  usePublishAgentContext(
    AGENT_CONTEXT.emails,
    publishContext ? context.emails : undefined,
  );
  const composer = useMemo((): EmbeddedComposerExtras => {
    const toggleSkill = (id: string) =>
      onChange({
        skillIds: selections.skillIds.includes(id)
          ? selections.skillIds.filter((other) => other !== id)
          : [...selections.skillIds, id],
      });
    const brandScopeNames = (brandScope ?? []).flatMap(
      (id) => brandProfiles?.find((profile) => profile.id === id)?.name ?? [],
    );
    const hasAttachments =
      selectedOptions.adTemplateFolders.length +
        selectedOptions.adTemplates.length +
        selectedOptions.emailTemplates.length +
        selectedOptions.emailComponents.length >
      0;
    return {
      onSent: () => {
        setSelections(clearSentSelections);
        setPromptChip(null);
      },
      // The draft and picks carry into the next new chat; a page's request stays with the chat it was made for.
      onClearSession: clearPromptChip,
      // Always a row, so "+" stays a menu rather than opening the file picker.
      leftActionItems: [
        skillsMenuItem({
          skills: options.skills,
          selectedIds: selections.skillIds,
          isLoading,
          disabledTooltip: emptyOptionsTooltip(
            "skills",
            options.skills.length,
            brandScope,
          ),
          onToggle: toggleSkill,
          // A new tab, so the chat and its draft stay where they are.
          onManage: () => openInNewTab(SKILLS_PATH),
        }),
      ],
      contextChips: [
        ...(promptChip
          ? [
              {
                id: "prompt",
                ...promptChip,
                removeLabel: `Remove ${promptChip.label}`,
                onRemove: () => setPromptChip(null),
              },
            ]
          : []),
        ...(contextChips ?? []),
        ...context.selectedSkills.map((skill) => ({
          id: `skill:${skill.id}`,
          label: skill.name,
          icon: ModelIcon,
          removeLabel: `Remove ${skill.name}`,
          onRemove: () => toggleSkill(skill.id),
        })),
      ],
      // Shown only once the page or a pick limits the options to some brands.
      eyebrow:
        brandScopeNames.length > 0 ? (
          <BrandScopePill names={brandScopeNames} page={pageBrand?.page} />
        ) : undefined,
      ...(channels.ad || channels.email
        ? {
            onInitialContext: (initial) =>
              onChange(selectionsFromInitialContext(initial)),
            interactionContext: toInteractionContext(
              selectedOptions,
              sendSettings,
            ),
            actions: (
              <ComposerControls
                options={options}
                selected={selectedOptions}
                sendSettings={sendSettings}
                brandScope={brandScope}
                isLoading={isLoading}
                onChange={onChange}
              />
            ),
            topContent: hasAttachments ? (
              <ComposerAttachments
                options={options}
                selected={selectedOptions}
                selections={selections}
                onChange={onChange}
              />
            ) : undefined,
          }
        : {}),
    };
  }, [
    promptChip,
    selections,
    selectedOptions,
    options,
    isLoading,
    context.selectedSkills,
    channels,
    sendSettings,
    brandScope,
    brandProfiles,
    pageBrand?.page,
    contextChips,
    onChange,
    openInNewTab,
    clearPromptChip,
  ]);
  const reset = useCallback(() => {
    setSelections(DEFAULT_SELECTIONS);
    setPromptChip(null);
  }, []);
  const onSessionChange = useCallback(
    (sessionId: string | null) => {
      if (sessionId === null) {
        reset();
      }
    },
    [reset],
  );
  return { composer, context, onSessionChange, reset };
}
